import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTableDetail, nextOrderState } from '../hooks/useTableDetail';
import type { OrderItem } from '../hooks/useTableDetail';
import ChannelBadge from '../components/ChannelBadge';
import AddItemSheet from '../components/AddItemSheet';
import { supabase } from '../services/supabase';

function relativeTime(iso: string): string {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000 / 60);
  if (diff < 1) return 'à l\'instant';
  return `${diff} min`;
}

function DgiStatus({ status }: { status: string | null }) {
  if (status === 'accepted') return <span className="text-success text-sm">✓ Facture fiscale générée</span>;
  if (status === 'error') return <span className="text-error text-sm">⚠ Erreur DGI</span>;
  if (status === 'pending' || status === 'submitted') return <span className="text-on-surface-variant text-sm">Facture en cours…</span>;
  return null;
}

async function callBillEdit(payload: Record<string, unknown>) {
  try {
    const { error } = await supabase.functions.invoke('tapnpay-bill-edit', { body: payload });
    if (error) throw error;
    return true;
  } catch {
    return false;
  }
}

function ItemRow({
  item,
  index,
  orderId,
  locked,
  onChanged,
}: {
  item: OrderItem;
  index: number;
  orderId: string;
  locked: boolean;
  onChanged: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [qty, setQty] = useState(item.qty);
  const [busy, setBusy] = useState(false);

  const handleAction = async (action: 'void' | 'remove' | 'edit_qty', newQty?: number) => {
    setBusy(true);
    setMenuOpen(false);
    await callBillEdit({
      action,
      order_id: orderId,
      payload: {
        item_id: item.id ?? null,
        item_index: index,
        quantity: newQty,
      },
    });
    setBusy(false);
    setEditing(false);
    onChanged();
  };

  return (
    <div
      className="px-4 py-2 min-h-[44px] flex items-center justify-between gap-3 relative"
      style={{ borderBottom: '0.5px solid #e5e7eb' }}
    >
      <div className="flex-1 min-w-0">
        {editing ? (
          <div className="flex items-center gap-2">
            <span className="text-sm text-on-surface truncate">{item.name}</span>
            <input
              type="number"
              min={0}
              value={qty}
              onChange={(e) => setQty(Number(e.target.value))}
              className="w-16 min-h-[32px] px-2 text-sm text-right bg-surface tabular-nums"
              style={{ border: '0.5px solid #000', outline: 'none' }}
            />
            <button
              type="button"
              disabled={busy}
              onClick={() => void handleAction('edit_qty', qty)}
              className="min-h-[32px] px-2 text-[11px] font-semibold text-on-primary bg-primary disabled:opacity-50"
            >
              OK
            </button>
            <button
              type="button"
              onClick={() => {
                setEditing(false);
                setQty(item.qty);
              }}
              className="min-h-[32px] px-2 text-[11px] text-on-surface-variant"
            >
              ✕
            </button>
          </div>
        ) : (
          <>
            <p className="text-sm text-on-surface">
              <span className="tabular-nums">{item.qty}×</span> {item.name}
            </p>
            {item.notes && (
              <p className="text-[11px] text-on-surface-variant italic">↳ {item.notes}</p>
            )}
          </>
        )}
      </div>
      <span className="text-sm tabular-nums text-on-surface shrink-0">
        {(item.qty * item.unit_price).toLocaleString('fr-MA', { minimumFractionDigits: 2 })} MAD
      </span>
      {!locked && !editing && (
        <button
          type="button"
          onClick={() => setMenuOpen((o) => !o)}
          className="min-h-[36px] min-w-[36px] flex items-center justify-center text-on-surface-variant"
          aria-label="Actions"
        >
          <span className="material-symbols-outlined text-[20px]">more_vert</span>
        </button>
      )}
      {menuOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
          <div
            className="absolute right-2 top-10 z-50 bg-surface min-w-[180px]"
            style={{ border: '0.5px solid #000' }}
          >
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setEditing(true);
                setMenuOpen(false);
              }}
              className="w-full text-left px-3 py-2 text-sm text-on-surface hover:bg-surface-container"
              style={{ borderBottom: '0.5px solid #e5e7eb' }}
            >
              Modifier la quantité
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void handleAction('void')}
              className="w-full text-left px-3 py-2 text-sm text-on-surface hover:bg-surface-container"
              style={{ borderBottom: '0.5px solid #e5e7eb' }}
            >
              Annuler item
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void handleAction('remove')}
              className="w-full text-left px-3 py-2 text-sm text-error hover:bg-surface-container"
            >
              Supprimer
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const STATE_LABELS: Record<string, string> = {
  pending: 'EN ATTENTE',
  confirmed: 'CONFIRMÉE',
  preparing: 'EN PRÉPARATION',
  ready: 'PRÊT',
  served: 'SERVI',
};

export default function TableDetailPage() {
  const { tableId } = useParams<{ tableId: string }>();
  const { session } = useAuth();
  const navigate = useNavigate();
  const { detail, loading, closeTable, updateTip, requestHelp, advanceState, refetch } =
    useTableDetail(tableId ?? '');
  const [editTip, setEditTip] = useState(false);
  const [tipValue, setTipValue] = useState('');
  const [closing, setClosing] = useState(false);
  const [helpSent, setHelpSent] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [advancing, setAdvancing] = useState(false);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-scanpay-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="p-6 text-sm text-on-surface-variant">Table introuvable.</div>
    );
  }

  const order = detail.current_order;
  const fiscalLocked = order?.fiscal_status === 'accepted';
  const ns = order ? nextOrderState(order.state ?? 'pending') : null;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header
        className="sticky top-0 z-30 bg-surface flex items-center gap-3 px-4 h-12"
        style={{ borderBottom: '0.5px solid #e5e7eb' }}
      >
        <button
          onClick={() => navigate(-1)}
          className="min-h-[40px] min-w-[40px] flex items-center justify-center text-on-surface-variant"
        >
          <span className="material-symbols-outlined text-[20px]">arrow_back</span>
        </button>
        <div>
          <p className="text-sm font-bold text-on-surface-strong">
            Table {detail.table_number}{detail.section ? ` · ${detail.section}` : ''}
          </p>
          <p className="text-[10px] text-on-surface-variant">{detail.capacity} personnes</p>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 p-4 space-y-4 pb-[80px]">
        {order ? (
          <>
            {/* Commande en cours */}
            <div className="bg-surface p-4 space-y-3" style={{ border: '0.5px solid #e5e7eb' }}>
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wider text-on-surface-variant">Commande en cours</p>
                <span className="text-xs text-on-surface-variant">Créée il y a {relativeTime(order.created_at)}</span>
              </div>

              {order.state && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-on-surface-variant">Statut</span>
                  <span
                    className="text-[11px] font-bold uppercase tracking-wider px-2 py-[3px]"
                    style={{ border: '0.5px solid #000' }}
                  >
                    {STATE_LABELS[order.state] ?? order.state}
                  </span>
                </div>
              )}

              {ns && !fiscalLocked && (
                <button
                  type="button"
                  disabled={advancing}
                  onClick={async () => {
                    setAdvancing(true);
                    await advanceState(order.id, ns);
                    setAdvancing(false);
                  }}
                  className="w-full min-h-[44px] text-[11px] font-semibold uppercase tracking-wider text-on-primary bg-primary disabled:opacity-50 inline-flex items-center justify-center gap-2"
                >
                  Avancer → {STATE_LABELS[ns] ?? ns}
                  <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
                </button>
              )}

              <div className="flex items-center gap-3">
                <span className="text-xs text-on-surface-variant">Paiement</span>
                <ChannelBadge channel={order.payment_method} />
              </div>

              <div className="flex items-center justify-between">
                <span className="text-xs text-on-surface-variant">Montant</span>
                <span className="text-lg font-bold tabular-nums text-on-surface-strong">
                  {order.total_amount.toLocaleString('fr-MA', { minimumFractionDigits: 2 })} MAD
                </span>
              </div>

              {order.employee_name && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-on-surface-variant">Serveur</span>
                  <span className="text-xs text-on-surface">{order.employee_name}</span>
                </div>
              )}

              <DgiStatus status={order.fiscal_status} />
            </div>

            {/* Articles */}
            <div className="bg-surface" style={{ border: '0.5px solid #e5e7eb' }}>
              <div
                className="px-4 py-2 flex items-center justify-between"
                style={{ borderBottom: '0.5px solid #e5e7eb' }}
              >
                <p className="text-xs font-semibold uppercase tracking-wider text-on-surface-variant">Articles</p>
                {fiscalLocked && (
                  <span className="text-[10px] uppercase tracking-wider text-on-surface-variant">
                    Édition verrouillée
                  </span>
                )}
              </div>
              {order.items.length === 0 && (
                <div className="px-4 py-3 text-sm text-on-surface-variant">Aucun article</div>
              )}
              {order.items.map((item, i) => (
                <ItemRow
                  key={`${item.id ?? item.name}-${i}`}
                  item={item}
                  index={i}
                  orderId={order.id}
                  locked={fiscalLocked}
                  onChanged={() => void refetch()}
                />
              ))}
              {!fiscalLocked && (
                <button
                  type="button"
                  onClick={() => setAddOpen(true)}
                  className="w-full px-4 py-3 text-left text-sm font-semibold uppercase tracking-wider text-on-surface inline-flex items-center gap-2"
                >
                  <span className="material-symbols-outlined text-[18px]">add</span>
                  Ajouter un article
                </button>
              )}
            </div>

            {/* Pourboire */}
            <div className="bg-surface p-4 flex items-center justify-between" style={{ border: '0.5px solid #e5e7eb' }}>
              <span className="text-xs text-on-surface-variant">Pourboire</span>
              {editTip ? (
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={tipValue}
                    onChange={(e) => setTipValue(e.target.value)}
                    placeholder="0"
                    className="w-24 min-h-[36px] px-2 text-sm text-on-surface text-right bg-surface"
                    style={{ border: '0.5px solid #e5e7eb', outline: 'none' }}
                    autoFocus
                  />
                  <span className="text-xs text-on-surface-variant">MAD</span>
                  <button
                    onClick={() => {
                      const tip = parseFloat(tipValue) || 0;
                      void updateTip(order.id, tip);
                      setEditTip(false);
                    }}
                    className="min-h-[36px] px-3 text-xs font-semibold text-on-primary bg-primary"
                  >
                    OK
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => { setTipValue(String(order.tip_amount)); setEditTip(true); }}
                  className="flex items-center gap-2 text-sm text-on-surface min-h-[36px]"
                >
                  <span className="tabular-nums">{order.tip_amount.toLocaleString('fr-MA', { minimumFractionDigits: 2 })} MAD</span>
                  <span className="material-symbols-outlined text-[16px] text-on-surface-variant">edit</span>
                </button>
              )}
            </div>
          </>
        ) : (
          <div
            className="bg-surface p-6 flex flex-col items-center gap-4"
            style={{ border: '0.5px solid #e5e7eb' }}
          >
            <span className="material-symbols-outlined text-[40px] text-on-surface-variant">table_restaurant</span>
            <p className="text-sm text-on-surface-variant">Aucune commande active</p>
            <button
              onClick={() => navigate(`/cash/${tableId ?? ''}`)}
              className="min-h-[48px] px-6 text-xs font-semibold uppercase tracking-wider text-on-surface bg-surface"
              style={{ border: '0.5px solid #e5e7eb' }}
            >
              Ouvrir une commande manuelle
            </button>
          </div>
        )}
      </div>

      {/* Footer sticky */}
      {order && (
        <footer
          className="fixed bottom-0 left-0 right-0 grid grid-cols-2 bg-surface"
          style={{ borderTop: '0.5px solid #e5e7eb' }}
        >
          <button
            onClick={() => {
              if (!session?.storeId) return;
              void requestHelp(session.storeId).then(() => setHelpSent(true));
              setTimeout(() => setHelpSent(false), 3000);
            }}
            className="min-h-[56px] flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-wider text-on-surface"
            style={{ borderRight: '0.5px solid #e5e7eb' }}
          >
            <span className="material-symbols-outlined text-[18px]">campaign</span>
            {helpSent ? 'Envoyé ✓' : 'Appeler aide'}
          </button>
          <button
            onClick={async () => {
              if (closing) return;
              setClosing(true);
              await closeTable(order.id);
              navigate(-1);
            }}
            disabled={closing}
            className="min-h-[56px] flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-wider text-on-primary bg-primary disabled:opacity-50"
          >
            <span className="material-symbols-outlined text-[18px]">check_circle</span>
            {closing ? '…' : 'Fermer la table'}
          </button>
        </footer>
      )}

      {order && session?.storeId && (
        <AddItemSheet
          storeId={session.storeId}
          orderId={order.id}
          open={addOpen}
          onClose={() => setAddOpen(false)}
          onAdded={() => void refetch()}
        />
      )}
    </div>
  );
}
